import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-black border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Link to="/" className="flex items-center space-x-3">
                <img
                  src="/horizonbg-logo.svg"
                  alt="HorizonBG"
                  className="h-8 w-auto"
                />
                <span className="text-xl font-semibold text-white">GP Ads AI</span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/signin"
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                className="bg-gp-sungold text-black hover:bg-gp-sungold-400 px-4 py-2 rounded-md text-sm font-semibold transition-colors"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="bg-gray-50 border-b border-gray-200 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-500">Last Updated: March 16, 2026</p>
          <p className="text-gray-500">Effective Date: March 16, 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-gray max-w-none">

          {/* Introduction */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Good Power LLC ("Good Power," "we," "us," or "our") operates the GP Ads AI platform
              ("Platform," "Service") available at <strong>gpads.io</strong>. This Privacy Policy
              describes how we collect, use, store, share, and protect information obtained through
              the Platform, including data accessed via third-party advertising platform integrations
              (Meta/Facebook, Google Ads, and TikTok).
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              By using GP Ads AI, you agree to the practices described in this Privacy Policy. If you
              do not agree, please do not use the Service.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We are committed to transparency, data minimization, and user control. This policy is
              designed to comply with the General Data Protection Regulation (GDPR), the California
              Consumer Privacy Act (CCPA), and the data use policies of Meta, Google, and TikTok.
            </p>
          </div>

          {/* Information We Collect */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Information You Provide</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>Account Information:</strong> Name, email address, organization name, and role when you create an account.</li>
              <li><strong>Organization Details:</strong> Business name, industry, team size, and billing information for your organization.</li>
              <li><strong>User-Generated Content:</strong> Campaign names, creative assets, ad copy, board names, notes, and other content you create within the Platform.</li>
              <li><strong>Communication Data:</strong> Messages sent through the AI Chat Assistant, support requests, and feedback.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">2.2 Advertising Platform Data (Via OAuth)</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you connect your advertising accounts via OAuth, we access the following data
              <strong> solely to provide the services you've requested</strong>:
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h4 className="font-semibold text-blue-900 mb-3">Meta (Facebook/Instagram) — Marketing API</h4>
              <p className="text-blue-800 text-sm mb-2">Permissions requested: <code>ads_read</code>, <code>ads_management</code>, <code>business_management</code></p>
              <ul className="list-disc pl-6 text-blue-800 space-y-1 text-sm">
                <li>Ad account metadata (account ID, name, currency, timezone)</li>
                <li>Campaign structure (campaigns, ad sets, ads — names, status, objectives, budgets)</li>
                <li>Performance metrics (impressions, clicks, spend, conversions, CTR, CPC, CPM, reach)</li>
                <li>Demographic breakdowns (aggregated age and gender performance data)</li>
                <li>Campaign management actions (pause, resume, budget adjustments — only when explicitly initiated by you)</li>
              </ul>
              <p className="text-blue-800 text-sm mt-2">
                <strong>We do NOT access:</strong> Personal user profiles, friend lists, messages, photos, or any data unrelated to advertising.
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
              <h4 className="font-semibold text-green-900 mb-3">Google Ads — Google Ads API</h4>
              <p className="text-green-800 text-sm mb-2">Scopes requested: <code>https://www.googleapis.com/auth/adwords</code></p>
              <ul className="list-disc pl-6 text-green-800 space-y-1 text-sm">
                <li>Campaign, ad group, and ad performance data</li>
                <li>Keyword performance metrics</li>
                <li>Budget and bidding information</li>
                <li>Conversion tracking data</li>
              </ul>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-6">
              <h4 className="font-semibold text-purple-900 mb-3">TikTok — Marketing API</h4>
              <p className="text-purple-800 text-sm mb-2">Scopes requested: <code>ads.read</code>, <code>ads.write</code></p>
              <ul className="list-disc pl-6 text-purple-800 space-y-1 text-sm">
                <li>Campaign, ad group, and ad performance data</li>
                <li>Creative performance metrics</li>
                <li>Audience insights (aggregated)</li>
                <li>Budget and scheduling data</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">2.3 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Usage Data:</strong> Pages visited, features used, actions taken within the Platform.</li>
              <li><strong>Device Information:</strong> Browser type, operating system, screen resolution, and IP address.</li>
              <li><strong>Cookies:</strong> Session cookies for authentication and essential functionality. We do not use tracking or advertising cookies.</li>
            </ul>
          </div>

          {/* How We Use Information */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We use collected information exclusively for:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li><strong>Providing the Service:</strong> Displaying your advertising data in unified dashboards, generating analytics, powering AI recommendations, and executing campaign management actions you initiate.</li>
              <li><strong>Data Normalization:</strong> Converting platform-specific data into a unified schema for cross-platform comparison and analysis.</li>
              <li><strong>AI-Powered Features:</strong> Generating ad copy suggestions, performance predictions, optimization opportunities, and trend analysis based on your campaign data.</li>
              <li><strong>Alerts and Notifications:</strong> Sending alerts about budget pacing, performance anomalies, and other configurable thresholds.</li>
              <li><strong>Audit and Compliance:</strong> Maintaining action logs and audit trails for governance and accountability.</li>
              <li><strong>Service Improvement:</strong> Analyzing aggregated, anonymized usage patterns to improve Platform features and performance.</li>
              <li><strong>Security:</strong> Detecting and preventing unauthorized access, fraud, and abuse.</li>
              <li><strong>Communication:</strong> Responding to support requests, sending service updates, and critical security notifications.</li>
            </ul>

            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-6">
              <h4 className="font-semibold text-yellow-900 mb-2">⚠️ What We Do NOT Do With Your Data</h4>
              <ul className="list-disc pl-6 text-yellow-800 space-y-1">
                <li>We do <strong>NOT</strong> sell your data to third parties.</li>
                <li>We do <strong>NOT</strong> use your advertising data for our own advertising purposes.</li>
                <li>We do <strong>NOT</strong> share your data with other customers or organizations.</li>
                <li>We do <strong>NOT</strong> use your data to build advertising profiles of end consumers.</li>
                <li>We do <strong>NOT</strong> transfer your advertising data to data brokers.</li>
                <li>We do <strong>NOT</strong> use your Meta, Google, or TikTok data for any purpose other than providing the GP Ads AI service to you.</li>
              </ul>
            </div>
          </div>

          {/* Data Storage and Security */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Storage and Security</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Infrastructure</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              All data is stored on Google Cloud Platform (GCP) infrastructure located in the United States:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>Transactional Data:</strong> Google Cloud SQL (PostgreSQL) with encryption at rest and in transit.</li>
              <li><strong>Analytics Data:</strong> Google BigQuery with organization-level data isolation.</li>
              <li><strong>Application Hosting:</strong> Google Cloud Run with auto-scaling and HTTPS-only access.</li>
              <li><strong>Static Assets:</strong> Google Cloud Storage with CDN delivery.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 OAuth Token Security</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you connect your advertising accounts, we handle your OAuth tokens with the highest security:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>Encryption at Rest:</strong> All OAuth access tokens are encrypted using pgcrypto in Cloud SQL. Tokens are never stored in plain text.</li>
              <li><strong>Encryption in Transit:</strong> All API communication uses TLS 1.2+ encryption.</li>
              <li><strong>Secret Management:</strong> Application secrets (App IDs, App Secrets) are stored in GCP Secret Manager, not in code or configuration files.</li>
              <li><strong>Token Never Logged:</strong> Access tokens are never written to application logs, error reports, or monitoring systems.</li>
              <li><strong>Automatic Refresh:</strong> Tokens are automatically refreshed before expiry to maintain uninterrupted service without requiring re-authorization.</li>
              <li><strong>Minimal Scope:</strong> We request only the permissions necessary to provide the features you use.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.3 Multi-Tenant Data Isolation</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              GP Ads AI is a multi-tenant platform. Your data is strictly isolated:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Every database query is filtered by your organization ID.</li>
              <li>BigQuery analytics are partitioned and clustered by organization.</li>
              <li>Row-level security ensures users can only access their authorized ad accounts.</li>
              <li>Your advertising data is never visible to other organizations on the Platform.</li>
            </ul>
          </div>

          {/* Data Sharing */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We share data only in the following limited circumstances:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Infrastructure Providers:</strong> Google Cloud Platform processes and stores data on our behalf under strict data processing agreements.</li>
              <li><strong>Advertising Platforms:</strong> When you initiate campaign management actions (pause, resume, budget changes), we send those instructions back to Meta, Google, or TikTok on your behalf.</li>
              <li><strong>Slack Notifications:</strong> If you configure Slack integration, alert summaries are sent to your designated Slack channels.</li>
              <li><strong>Legal Requirements:</strong> We may disclose data if required by law, subpoena, court order, or to protect our rights, safety, or property.</li>
              <li><strong>Business Transfer:</strong> In the event of a merger, acquisition, or sale of assets, data may be transferred to the acquiring entity. You will be notified of any such transfer.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>We do not share, sell, rent, or trade your advertising data with any third parties for their own purposes.</strong>
            </p>
          </div>

          {/* Your Rights */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights and Choices</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 Disconnect Advertising Accounts</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You can disconnect any advertising account at any time through the Ad Account Manager
              in Settings. Disconnecting an account will:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li>Immediately revoke the OAuth access token</li>
              <li>Stop all data collection for that account</li>
              <li>Remove the connection from our system</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-6">
              You can also revoke access directly from the platform's settings:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>Meta:</strong> Facebook Settings → Security and Login → Apps and Websites → Remove GP Ads AI Platform</li>
              <li><strong>Google:</strong> Google Account → Security → Third-party apps with account access → Remove GP Ads AI</li>
              <li><strong>TikTok:</strong> TikTok For Business → Settings → Authorized Apps → Revoke GP Ads AI</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">6.2 Data Deletion</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You have the right to request deletion of your data at any time. To request data deletion:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li>Email <a href="mailto:privacy@gpads.io" className="text-blue-600 hover:underline">privacy@gpads.io</a> with "Data Deletion Request" in the subject line</li>
              <li>Include your organization name and account email</li>
              <li>We will process deletion requests within 30 days</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-6">
              Upon deletion, we will remove:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li>Your account and profile information</li>
              <li>All stored OAuth tokens (encrypted and plaintext)</li>
              <li>Historical advertising data from BigQuery</li>
              <li>Campaign management records and audit logs</li>
              <li>AI-generated content and suggestions</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">6.3 GDPR Rights (EU Users)</h3>
            <p className="text-gray-700 leading-relaxed mb-4">If you are in the European Economic Area, you have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Correct inaccurate personal data</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Restriction:</strong> Limit how we process your data</li>
              <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">6.4 CCPA Rights (California Users)</h3>
            <p className="text-gray-700 leading-relaxed mb-4">If you are a California resident, you have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Know:</strong> What personal information we collect, use, and disclose</li>
              <li><strong>Delete:</strong> Request deletion of your personal information</li>
              <li><strong>Opt-Out:</strong> Opt-out of the sale of personal information (we do not sell personal information)</li>
              <li><strong>Non-Discrimination:</strong> Not receive discriminatory treatment for exercising your rights</li>
            </ul>
          </div>

          {/* Data Retention */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Data Type</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Retention Period</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="px-4 py-3 text-sm text-gray-700">Account information</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Until account deletion</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Deleted within 30 days of request</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700">Advertising performance data</td>
                    <td className="px-4 py-3 text-sm text-gray-700">2 years</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Rolling window for historical analytics</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 text-sm text-gray-700">OAuth tokens</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Until disconnection</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Deleted immediately upon account disconnect</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700">Audit logs</td>
                    <td className="px-4 py-3 text-sm text-gray-700">2 years</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Required for compliance and governance</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 text-sm text-gray-700">AI-generated content</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Until account deletion</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Copy suggestions, recommendations</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700">Server logs</td>
                    <td className="px-4 py-3 text-sm text-gray-700">90 days</td>
                    <td className="px-4 py-3 text-sm text-gray-700">For debugging and security monitoring</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Platform-Specific Compliance */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Platform-Specific Data Use Compliance</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">8.1 Meta Platform Terms</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our use of data received from Meta APIs complies with the{' '}
              <a href="https://developers.facebook.com/terms/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                Meta Platform Terms
              </a>{' '}
              and{' '}
              <a href="https://developers.facebook.com/policy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                Developer Policies
              </a>.
              Specifically:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li>We only access data necessary to provide the features you use.</li>
              <li>We do not use Meta data for any purpose other than providing GP Ads AI services.</li>
              <li>We process deauthorize callbacks and data deletion requests promptly.</li>
              <li>We do not cache Meta data beyond what is necessary for Platform functionality.</li>
              <li>We do not sell or license Meta data to third parties.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">8.2 Google API Services User Data Policy</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our use of information received from Google APIs adheres to the{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                Google API Services User Data Policy
              </a>,
              including the Limited Use requirements.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">8.3 TikTok Marketing API Terms</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our use of TikTok Marketing API data complies with the{' '}
              <a href="https://business-api.tiktok.com/portal/docs" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                TikTok Marketing API Terms of Service
              </a>.
            </p>
          </div>

          {/* Cookies */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Cookies and Tracking</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We use only essential cookies:</p>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Cookie</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Purpose</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="px-4 py-3 text-sm text-gray-700 font-mono">gp_session</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Authentication session</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Session / 24 hours</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700 font-mono">gp_csrf</td>
                    <td className="px-4 py-3 text-sm text-gray-700">CSRF protection</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Session</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700 font-mono">gp_preferences</td>
                    <td className="px-4 py-3 text-sm text-gray-700">UI preferences (dark mode, layout)</td>
                    <td className="px-4 py-3 text-sm text-gray-700">1 year</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-gray-700 leading-relaxed mt-4">
              We do <strong>not</strong> use third-party tracking cookies, advertising cookies, or analytics trackers
              that share data with external parties.
            </p>
          </div>

          {/* Children */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              GP Ads AI is a business-to-business platform and is not intended for use by individuals under 18 years of age.
              We do not knowingly collect personal information from children. If you believe a child has provided us with
              personal information, please contact us at{' '}
              <a href="mailto:privacy@gpads.io" className="text-blue-600 hover:underline">privacy@gpads.io</a>.
            </p>
          </div>

          {/* International */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              Data is processed and stored in the United States on Google Cloud Platform infrastructure.
              If you are accessing the Platform from outside the United States, your data will be transferred to
              and processed in the US. We rely on Google Cloud's compliance frameworks (SOC 2, ISO 27001) and
              standard contractual clauses for international data transfers where required.
            </p>
          </div>

          {/* Changes */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of material changes by
              posting a prominent notice on the Platform or sending an email to the address associated with your account.
              The "Last Updated" date at the top of this page indicates when the policy was last revised.
              Your continued use of the Service after changes are posted constitutes acceptance of the revised policy.
            </p>
          </div>

          {/* Contact */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions about this Privacy Policy, your data, or your rights, contact us:
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <p className="text-gray-700 mb-1"><strong>Good Power LLC</strong></p>
              <p className="text-gray-700 mb-1">GP Ads AI Privacy Team</p>
              <p className="text-gray-700 mb-1">Email: <a href="mailto:privacy@gpads.io" className="text-blue-600 hover:underline">privacy@gpads.io</a></p>
              <p className="text-gray-700">Website: <a href="https://gpads.io" className="text-blue-600 hover:underline">https://gpads.io</a></p>
            </div>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <img src="/horizonbg-logo.svg" alt="HorizonBG" className="h-6 w-auto" />
              <span className="text-white font-semibold">GP Ads AI</span>
            </div>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm">Terms of Service</Link>
              <Link to="/" className="text-gray-400 hover:text-white text-sm">Home</Link>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-6 text-center text-sm">
            <p>&copy; 2026 Good Power LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
